<template xmlns="http://www.w3.org/1999/html">
    <form
        method=""
        action=""
        class="form-group"
        @submit.prevent="onSubmit"
        @keydown="getCurrentForm.errors?.clear($event.target.name)"
        ref="form"
        autocomplete="off"
    >
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>

        <div class="progress-bar-container">
            <ProgressBar
                :active-step="currentStepNumber"
                :total-steps="totalSteps"
            ></ProgressBar>
        </div>

        <component
            :is="{...currentStepComponent}"
            ref="currentStepRef"
            :course="course"
            :step="currentStep"
            :fields="currentStepFields"
            :form-data="formData[currentStepNumber - 1] || sessionOnLoad"
            :previous-step-form-data="formData[currentStepNumber - 1] || sessionOnLoad"
            @codeOfConductUpdated="checked => setButtonDisabled(!checked)"
        ></component>

        <div class="form__buttons form-group py-3">
            <button
                type="button"
                class="btn btn-secondary me-2"
                data-cy="btn-back"
                @click="goBack"
            >
                Go Back
            </button>
            <button :disabled="isButtonDisabled" class="btn btn-primary" data-cy="btn-next">
                {{ currentStepNumber === totalSteps ? "Submit" : "Next" }}
            </button>
        </div>
        <p
            class="form-error"
            role="alert"
            aria-live="assertive"
            v-if="getCurrentForm && getCurrentForm.errors?.get('application-status')"
        >
            {{ getCurrentForm.errors.get("application-status") }}
        </p>
        <div v-if="supportMessage !== null">
            <h3>{{ supportMessage.title }}</h3>
            <div v-html="supportMessage.items[0]" />
        </div>
    </form>
</template>

<script setup>
import axios from "axios";
import ProgressBar from "./ProgressBar.vue";
import StepEducation from "./Profile/StepEducation";
import StepBenefits from "./Profile/StepBenefits";
import StepCircumstances from "./Profile/StepCircumstances";
import StepEthnicity from "./Profile/StepEthnicity";
import StepGender from "./Profile/StepGender";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

const props = defineProps({
    steps: Array,
    course: String,
    sessionOnLoad: Object,
    supportMessage: Object
})

const currentStepRef = useTemplateRef('currentStepRef')
const previousStepNumber = ref(currentStepNumber)
const formRef = useTemplateRef('form')
const totalSteps = props.steps.length
const formData = ref(Array(totalSteps))
let currentStepNumber = ref(1)
const isButtonDisabled = ref(false)
let currentStepComponent = ref()
const isLoading = ref(false)


let stepIdToComponentMappings = {
    education: StepEducation,
    benefits: StepBenefits,
    circumstances: StepCircumstances,
    ethnicity: StepEthnicity,
    gender: StepGender
};

window.onpopstate = (event) => {
    loadStepFromUrl();
};

loadStepFromUrl();

watch(currentStepNumber, () => {
    currentStep.value = props.steps[currentStepNumber.value - 1]

    formData.value[
        currentStepNumber.value - 1
    ] = getCurrentForm().data();
})

let currentStep = ref(props.steps[currentStepNumber.value - 1])

let stepComponents = computed(() => {
    return props.steps.map((step) => {
        return stepIdToComponentMappings[step.id];
     });
})

currentStepComponent = computed(() => {
    return stepComponents.value[currentStepNumber.value - 1]
}) 

const currentStepFields = computed(() => {
    let originalFields = currentStep.value.fields;
    for (const fieldName in originalFields) {
        let field = originalFields[fieldName];
        if (field.validation && Array.isArray(field.validation)) {
            field.required = field.validation.includes("required");

            const requiredIfRules = field.validation.filter((s) =>
                s.includes("required_if")
            );

            if (requiredIfRules.length > 0) {
                field.requiredIfRules = requiredIfRules.map(rule => {
                    const [field, ...values] = rule.replace("required_if:", "").split(",");
                    return {field, values}
                })
            }
        }
    }
    return originalFields;
})

nextTick(() => {
    window.dataLayer = window.dataLayer || [];
})

function getCurrentForm() {
    return currentStepRef.value.formRef;
}

function loadStepFromUrl() {
    const url = new URL(window.location);
    let parsedStepNumber = Number.parseInt(
        url.searchParams.get("step")
    );

    currentStepNumber.value = parsedStepNumber || 1;
}

function addStepToUrl() {
    const url = new URL(window.location);
    url.searchParams.set("step", currentStepNumber.value);
    window.history.pushState({}, "", url);
}

function scrollToBeginning() {
    const form = formRef.value;

    if (form) {
        const { top } = form.getBoundingClientRect();
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo(0, offsetY);
    }
}

function scrollToFirstError() {
    const form = getCurrentForm();
    if (!form || !form.errors.any()) {
        console.log(form.errors);
        return;
    }
    const error = document.getElementsByClassName('form-error')[0];
    if (error) {
        const parent = error.parentNode;
        const {top} = parent.getBoundingClientRect();
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo({top: offsetY, behavior: 'smooth'});
    }
}

function onSubmit() {
    isLoading.value = true;

    if (currentStepNumber.value !== totalSteps) {
        window.dataLayer.push({'event': 'getInto_profile_step_' + currentStepNumber + '_submitted'});
    } else {
        window.dataLayer.push({'event': 'getInto_profile_form_completed'});
    }

    formData.value[
        currentStepNumber.value - 1
    ] = getCurrentForm().data();

    return axios
        .post(`/courses/${props.course}/profile/${currentStep.value.id}`, getCurrentForm().data())
        .then(() => {
            goForward();
        })
        .catch((err) => {
            if (err.response && err.response.status === 422) {
                getCurrentForm().errors.record(err.response.data.errors);
                scrollToFirstError()
            } else {
                console.log(err)
                window.location.href = "/site-error"
            }
        })
        .finally(() => (isLoading.value = false));
}

function goBack() {
    previousStepNumber.value = currentStepNumber - 1;
    window.dataLayer.push({'event': 'getInto_profile_step_' + previousStepNumber.value + '_returned'})
    if (currentStepNumber.value <= 1) {
        window.location.href = `/${props.course}-profile`;
    } else {
        currentStepNumber.value--;
        addStepToUrl();
        scrollToBeginning();
    }
}

function goForward() {
    if (currentStepNumber.value < totalSteps) {
        currentStepNumber.value++;
        addStepToUrl();
        scrollToBeginning();
    } else {
        window.location.href = `/${props.course}-profile-completed`;
    }
}
</script>
